import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import flatpickr from 'flatpickr';
import { French } from 'flatpickr/dist/l10n/fr';
import { PosDateFilterBase } from '../pos-date-filter.base';
import { LcPosListFilters } from '../logiciel-caisse-pos-list.types';

@Component({
  selector: 'app-logiciel-caisse-pos-filters',
  templateUrl: './logiciel-caisse-pos-filters.component.html',
  styleUrls: ['./logiciel-caisse-pos-filters.component.scss'],
})
export class LogicielCaissePosFiltersComponent
  extends PosDateFilterBase
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() showDateFilter = true;
  @Input() showWarehouseFilter = true;
  @Input() searchPlaceholder = 'Recherche par mot clé';
  @Input() warehousePlaceholder = 'Tous les entrepôts';
  @Input() warehouseSelectId = 'lcPosWarehouseSelect';

  @Output() filtersChange = new EventEmitter<LcPosListFilters>();

  searchTerm = '';
  warehouseOptions: { id: number; label: string }[] = [];
  selectedWarehouseIds: number[] = [];

  private searchDebounce?: ReturnType<typeof setTimeout>;

  constructor(cdr: ChangeDetectorRef) {
    super(cdr);
  }

  ngOnInit(): void {
    if (this.showDateFilter) {
      this.initDateFilters();
    }
    setTimeout(() => this.emitFilters(), 0);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (this.showDateFilter) {
      this.initializeFlatpickr();
    }
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  }

  setWarehouseOptions(options: { id: number; label: string }[]): void {
    this.warehouseOptions = options;
    this.cdr.detectChanges();
    this.refreshFeatherIcons();
  }

  protected onDateRangeChanged(): void {
    this.emitFilters();
  }

  onSearch(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(() => this.emitFilters(), 300);
  }

  onWarehouseFilterChange(): void {
    this.emitFilters();
    this.refreshFeatherIcons();
  }

  getActiveFilters(): { key: string; label: string; value: string }[] {
    const filters: { key: string; label: string; value: string }[] = [];
    if (this.searchTerm.trim()) {
      filters.push({ key: 'search', label: 'Recherche', value: this.searchTerm.trim() });
    }
    if (this.selectedWarehouseIds.length > 0) {
      const labels = this.selectedWarehouseIds
        .map((id) => this.warehouseOptions.find((o) => o.id === id)?.label ?? String(id))
        .join(', ');
      filters.push({ key: 'warehouse', label: 'Entrepôt', value: labels });
    }
    return filters;
  }

  trackByFilterKey(_index: number, filter: { key: string }): string {
    return filter.key;
  }

  removeFilter(key: string, event?: Event): void {
    event?.stopPropagation();
    if (key === 'search') {
      this.searchTerm = '';
    } else if (key === 'warehouse') {
      this.selectedWarehouseIds = [];
    }
    this.emitFilters();
    this.refreshFeatherIcons();
  }

  emitFilters(): void {
    this.filtersChange.emit({
      dateDebut: this.dateFilterStart,
      dateFin: this.dateFilterEnd,
      searchTerm: this.searchTerm.trim(),
      warehouseIds: [...this.selectedWarehouseIds],
    });
  }

  private initializeFlatpickr(): void {
    if (!this.datePicker?.nativeElement) {
      return;
    }
    flatpickr(this.datePicker.nativeElement, {
      locale: French,
      mode: 'range',
      dateFormat: 'Y-m-d',
      defaultDate: [this.dateFilterStart, this.dateFilterEnd],
      onChange: (selectedDates) => {
        if (selectedDates.length === 2) {
          const startDate = selectedDates[0].toISOString().split('T')[0];
          const endDate = selectedDates[1].toISOString().split('T')[0];
          this.dateFilterStart = startDate;
          this.dateFilterEnd = endDate;
          this.selectedDay = startDate === endDate ? startDate : '';
          this.selectedDate = selectedDates[0];
          this.selectedMonthIndex = null;
          this.selectedYear = null;
          if (startDate === endDate) {
            this.viewMode = 'jours';
            this.updateSelectedPeriod();
          } else {
            this.selectedPeriod = `${this.formatDateFr(startDate)} - ${this.formatDateFr(endDate)}`;
          }
          this.onDateRangeChanged();
        } else if (selectedDates.length === 1) {
          const single = selectedDates[0].toISOString().split('T')[0];
          this.dateFilterStart = single;
          this.dateFilterEnd = single;
          this.selectedDay = single;
          this.selectedDate = selectedDates[0];
          this.updateSelectedPeriod();
          this.onDateRangeChanged();
        }
        this.cdr.detectChanges();
      },
    });
  }

  private formatDateFr(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR');
  }
}
